import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { ArrowRightIcon } from '@gto/icons-react/outline/arrow-right'
import { WalletIcon } from '@gto/icons-react/outline/wallet'
import { Button } from '@gto/ui/button'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import type { AffiliateNextBatchResponseSchema } from '@/api/gen'
import { StatsCard } from '@/components/stats-card'
import { toAmount } from '@/utils/to-amount'

const UsdAmount = ({ value }: { value: number }) => (
  <FormattedNumber
    currency="USD"
    maximumFractionDigits={0}
    style="currency"
    value={value}
  />
)

type NextBatchCardProps = {
  data: AffiliateNextBatchResponseSchema | undefined
  loading?: boolean
}

type TierRowProps = {
  label: string
  partnerCount: number
  amount: number
  fraction: number
}

const TierRow = ({ label, partnerCount, amount, fraction }: TierRowProps) => {
  const { formatMessage } = useIntl()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <span className="size-2 shrink-0 rounded-full bg-primary-main" />
          <Typography
            className="font-medium"
            variant="body2"
          >
            {label}
          </Typography>
        </span>
        <Typography
          className="font-medium"
          variant="body2"
        >
          <UsdAmount value={amount} />
        </Typography>
      </div>
      <div
        aria-hidden
        className="h-1.5 w-full overflow-hidden rounded-full bg-paper-elevation4"
      >
        <div
          className="h-full rounded-full bg-primary-main"
          style={{ width: `${Math.max(2, Math.round(fraction * 100))}%` }}
        />
      </div>
      <Typography
        color="text.secondary"
        variant="caption"
      >
        {formatMessage({ id: 'admin.dashboard.nextBatch.tierPartners' }, { count: partnerCount })}
      </Typography>
    </div>
  )
}

export const NextBatchCard = ({ data, loading = false }: NextBatchCardProps) => {
  const { formatMessage } = useIntl()
  const totalAmount = toAmount(data?.total_amount, 'next-batch total_amount:')
  const byTier = data?.by_tier ?? []
  const maxTierAmount = byTier.reduce(
    (max, tier) => Math.max(max, toAmount(tier.amount, 'next-batch tier.amount:')),
    0,
  )

  return (
    <StatsCard
      icon={<WalletIcon />}
      title={<FormattedMessage id="admin.dashboard.nextBatch.title" />}
      controls={
        <Button
          as={Link}
          size="small"
          to="/admin/settlements"
          variant="text"
          endIcon={
            <ArrowRightIcon
              height={16}
              width={16}
            />
          }
        >
          <FormattedMessage id="admin.dashboard.nextBatch.review" />
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <Skeleton
            height={40}
            variant="rectangular"
            width={160}
          />
        ) : (
          <div className="flex flex-col">
            <Typography
              className="font-heading font-semibold leading-100"
              variant="h4"
            >
              <UsdAmount value={totalAmount} />
            </Typography>
            <Typography
              color="text.secondary"
              variant="body2"
            >
              {formatMessage(
                { id: 'admin.dashboard.nextBatch.partners' },
                { count: data?.partner_count ?? 0 },
              )}
            </Typography>
          </div>
        )}

        {!loading && byTier.length > 0 && (
          <div className="flex flex-col gap-3">
            {byTier.map((tier) => {
              const tierAmount = toAmount(tier.amount, 'next-batch tier.amount:')
              const label =
                tier.tier_name ?? formatMessage({ id: 'admin.dashboard.nextBatch.noTier' })

              return (
                <TierRow
                  key={tier.tier_name ?? 'untiered'}
                  amount={tierAmount}
                  fraction={maxTierAmount > 0 ? tierAmount / maxTierAmount : 0}
                  label={label}
                  partnerCount={tier.partner_count}
                />
              )
            })}
          </div>
        )}

        {!loading && byTier.length === 0 && (
          <Typography
            className="py-2"
            color="text.secondary"
            variant="body2"
          >
            <FormattedMessage id="admin.dashboard.nextBatch.empty" />
          </Typography>
        )}
      </div>
    </StatsCard>
  )
}
